import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import {
    fetchLeadsForms,
    fetchLeadsHistoryData,
    fetchCompanies,
} from "../../../core/data/redux/leads-queue/thunks";
import {
    selectActiveCompany,
    selectPageLoading,
    selectQueueFormId,
} from "../../../core/data/redux/leads-queue/selectors";
import LeadsInQueueHistoryHeader from "./components/leads-in-queue-history/LeadsInQueueHistoryHeader";
import LeadsHistoryFilters from "./components/leads-in-queue-history/LeadsHistoryFilters";
import LeadsHistoryTable from "./components/leads-in-queue-history/LeadsHistoryTable";
import LeadsQueueFooter from "./components/leads-in-queue/LeadsQueueFooter";
import Loader from "../../../core/common/loader";
import { DEFAULT_FILTER } from "../../../core/data/redux/leads-queue/constants";
import type { DateRange, SourceFilter, SourceHierarchy, LeadHistory, Pagination, Employee } from "../../../core/data/redux/leads-queue/types";
import { exportHistoryData } from "../../../core/data/redux/services/leadsQueueApi";
import { notify } from "../../../services/toast.config";

const DEFAULT_DATE_RANGE: DateRange = { start_date: null, end_date: null };
const DEFAULT_PAGINATION: Pagination = { total: 0, current_page: 1, per_page: 30, total_pages: 0 };

const LeadsHistoryPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const activeCompany = useSelector(selectActiveCompany);
    const formId = useSelector(selectQueueFormId);
    const pageLoading = useSelector(selectPageLoading);
    const [tableLoading, setTableLoading] = useState(false);
    const [isExportStarted, setIsExportStarted] = useState(false);
    const [isAllForms, setIsAllForms] = useState(false);

    // State
    const [activeSourceType, setActiveSourceType] = useState<string>("all");
    const [sourceFilter, setSourceFilter] = useState<SourceFilter>(DEFAULT_FILTER);
    const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [assignedTo, setAssignedTo] = useState<string | null>(null);

    // Data State
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [sourceHierarchy, setSourceHierarchy] = useState<SourceHierarchy | null>(null);
    const [leadsHistory, setLeadsHistory] = useState<LeadHistory[]>([]);
    const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);

    const effectiveFormId = isAllForms ? null : formId;
    const hasFormScope = isAllForms || Boolean(formId);

    useEffect(() => { dispatch(fetchCompanies()); }, [dispatch]);

    useEffect(() => {
        if (!activeCompany.company_id) return;
        setActiveSourceType("all");
        setSourceFilter(DEFAULT_FILTER);
        setDateRange(DEFAULT_DATE_RANGE);
        setAssignedTo(null);
        setIsAllForms(false);
        setLeadsHistory([]);
        dispatch(fetchLeadsForms({ companyId: activeCompany.company_id, companyType: activeCompany.company_type }));
    }, [dispatch, activeCompany.company_id, activeCompany.company_type]);

    const loadData = async (
        page = 1,
        limit = 30,
        sourceTypeOverride?: string,
        filterOverride?: SourceFilter,
        dateOverride?: DateRange,
        searchOverride?: string,
        assignedToOverride?: string | null
    ) => {
        if (!activeCompany.company_id || !hasFormScope) return;

        setTableLoading(true);
        try {
            const data = await dispatch(fetchLeadsHistoryData({
                companyId: activeCompany.company_id,
                companyType: activeCompany.company_type,
                sourceType: sourceTypeOverride ?? activeSourceType,
                sourceFilter: filterOverride ?? sourceFilter,
                dateRange: dateOverride ?? dateRange,
                formId: effectiveFormId,
                page,
                limit,
                search: searchOverride !== undefined ? searchOverride : searchQuery,
                assignedTo: assignedToOverride !== undefined ? assignedToOverride : assignedTo
            })).unwrap();

            setLeadsHistory(data.leads);
            setPagination(data.pagination);
            setEmployees(data.employees || []);
            // Merged Hierarchy Data
            setSourceHierarchy({
                facebook: data.hierarchy?.facebook || [],
                whatsapp: data.hierarchy?.whatsapp || [],
                public_form: data.hierarchy?.public_form || [],
                crm: { count: data.counts?.crm || 0 },
                unknown: { count: data.counts?.unknown || 0 },
                counts: data.counts
            });
        } finally {
            setTableLoading(false);
        }
    };

    useEffect(() => {
        if (activeCompany.company_id && hasFormScope) {
            loadData(1, 30);
        }
    }, [activeCompany.company_id, activeCompany.company_type, formId, isAllForms]);

    const handleReset = () => {
        setActiveSourceType("all");
        setSourceFilter(DEFAULT_FILTER);
        setDateRange(DEFAULT_DATE_RANGE);
        setSearchQuery("");
        setAssignedTo(null);
        loadData(1, 30, "all", DEFAULT_FILTER, DEFAULT_DATE_RANGE, "", null);
    };

    const handleExport = async () => {
        try {
            notify.success("Download Started")
            setIsExportStarted(true)
            const signature = localStorage.getItem("signature");
            const blob = await exportHistoryData(
                signature,
                activeCompany.company_id,
                activeCompany.company_type,
                activeSourceType,
                sourceFilter,
                dateRange,
                effectiveFormId,
                searchQuery,
                assignedTo
            );

            // Create download link dynamically
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Leads_History_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Export failed", error);
        } finally {
            setIsExportStarted(false)
        }
    };

    return (
        <>
            {pageLoading && <Loader />}
            <div className="page-wrapper">
                <div className="content">
                    <LeadsInQueueHistoryHeader
                        isExportStarted={isExportStarted}
                        handleExport={handleExport}
                        isAllForms={isAllForms}
                        onAllFormsChange={setIsAllForms}
                    />
                    <LeadsHistoryFilters
                        activeCompany={activeCompany}
                        employees={employees}
                        hasFormScope={hasFormScope}
                        activeSourceType={activeSourceType}
                        sourceTypeCounts={sourceHierarchy?.counts ?? null}
                        sourceHierarchy={sourceHierarchy}
                        sourceFilter={sourceFilter}
                        dateRange={dateRange}
                        isLoading={tableLoading}
                        displayTotalCount={pagination.total}
                        activeAssignedTo={assignedTo}
                        onSourceTypeChange={(key) => {
                            setActiveSourceType(key);
                            setSourceFilter(DEFAULT_FILTER);
                            loadData(1, 30, key, DEFAULT_FILTER);
                        }}
                        onSourceFilterChange={(filter) => {
                            setSourceFilter(filter);
                            loadData(1, 30, undefined, filter);
                        }}
                        onDateRangeChange={(dr) => {
                            setDateRange(dr);
                            loadData(1, 30, undefined, undefined, dr);
                        }}
                        onSearch={(term) => {
                            setSearchQuery(term);
                            loadData(1, 30, undefined, undefined, undefined, term);
                        }}
                        onAssignedToChange={(userId) => {
                            setAssignedTo(userId);
                            loadData(1, 30, undefined, undefined, undefined, undefined, userId);
                        }}
                        onReset={handleReset}
                    />
                    <LeadsHistoryTable
                        leadsHistory={leadsHistory}
                        pagination={pagination}
                        tableLoading={tableLoading}
                        showFormColumn={isAllForms}
                        onPageChange={(page) => loadData(page, pagination.per_page)}
                    />
                </div>
                <LeadsQueueFooter />
            </div>
        </>
    );
};
export default LeadsHistoryPage;
